import React, { ReactElement, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import { Box, Stack } from '@mui/material';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { useNavigate, useParams } from 'react-router-dom';
import TrainingSteps from 'modules/training/training-programs/forms/TrainingStep';
import AchievementForm from 'modules/training/training-programs/forms/AchievementForm';
import CertificationForm from 'modules/training/training-programs/forms/CertificationForm';
import BasicInformationForm from 'modules/training/training-programs/forms/BasicInformationForm';
import RiskManagementForm from 'modules/training/training-programs/forms/RiskManagementForm';
import { TrainingPorgramsService } from 'services/TrainingProgramService';
import { Toast, Utils } from 'shared/helpers';
import { Formik } from 'formik';
import TrainingContent from '../forms/TraningContent';
import { TrainingProgramsScheme } from 'validators/TrainingProgramsScheme';
import PageHeader from 'shared/components/PageHeader';

const AddTrainingPrograms = ({
  purpose = null,
  defaultData = null,
}: any): ReactElement => {
  const [defaultProgramData, setDefaultProgramData] =
    useState<any>(defaultData);
  const [loadingProgramData, setLoadingProgramData] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(true);
  const navigate = useNavigate();
  const { templateId, trainingId } = useParams();
  const [title, setTitle] = useState('Create training program');
  const handleSubmittedValues = (submittedValues: any, formik: any): void => {
    formik.setSubmitting(true);
    const {
      title,
      description,
      content,
      video_url,
      format,
      ai_rmf_domains,
      prerequisites,
      learning_objectives,
      status,
      version,
    } = submittedValues;

    const duration_minutes = submittedValues.duration;
    const target_roles = submittedValues.role;
    const is_mandatory = submittedValues.mandatory;
    const recurrence_months = parseFloat(submittedValues.recurrence_months);
    const certification_valid_months = parseFloat(
      submittedValues.certification_valid_months
    );

    const training_definition = {
      steps: submittedValues.training_definition,
    };

    const payload = {
      program_data: {
        title,
        description,
        content,
        video_url,
        duration_minutes,
        format,
        ai_rmf_domains,
        target_roles,
        prerequisites,
        learning_objectives,
        status,
        version,
        is_mandatory,
        training_definition,
        recurrence_months,
        certification_valid_months,
      },
    };

    if (purpose === 'editing') {
      TrainingPorgramsService.updateTrainingProgram(payload, trainingId)
        .then(() => {
          Toast.showSuccessMessage('Training program updated successfully.');
          navigate('/training-programs');
        })
        .catch((error) => {
          Utils.handleErrorResponse(error);
        });
      return;
    }

    TrainingPorgramsService.createTrainingProgram(payload)
      .then(() => {
        Toast.showSuccessMessage('Training program created successfully.');
        navigate('/training-programs');
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        formik.setSubmitting(false);
      });
  };

  useEffect(() => {
    if (!purpose) {
      setLoadingProgramData(false);
      return;
    }
    if (templateId) {
      TrainingPorgramsService.getTrainingDetailsByID(templateId)
        .then((response) => {
          const { data } = response.data;
          setDefaultProgramData(data);
          setTitle(data.title);
          setLoadingProgramData(false);
        })
        .catch((error) => {
          Utils.handleErrorResponse(error);
        });
    }
    if (trainingId) {
      TrainingPorgramsService.getTrainingProgramDetails(trainingId)
        .then((response) => {
          const { data } = response.data;
          setDefaultProgramData(data.program_data);
          setTitle(data.program_data.title);
          setLoadingProgramData(false);
        })
        .catch((error) => {
          Utils.handleErrorResponse(error);
        });
    }
  }, [templateId]);

  return (
    <>
      <PageHeader
        title={title}
        description="Configure your training program details."
      />
      <Box className="training form-training-programs">
        <HeadTag
          title={
            purpose === 'editing'
              ? 'Edit Training Programs'
              : 'Create Training Programs'
          }
        />

        {!loadingProgramData && (
          <>
            <Formik
              initialValues={
                purpose === 'ready-to-use' || purpose === 'editing'
                  ? {
                      // basic
                      title: defaultProgramData?.title,
                      duration: defaultProgramData?.duration_minutes,
                      description: defaultProgramData?.description,
                      likelihood: '',
                      format: defaultProgramData?.format
                        ? defaultProgramData.format.charAt(0).toUpperCase() +
                          defaultProgramData.format.slice(1)
                        : '',
                      status: defaultProgramData?.status
                        ? defaultProgramData.status.charAt(0).toUpperCase() +
                          defaultProgramData.status.slice(1)
                        : '',
                      mandatory: defaultProgramData?.is_mandatory,
                      video_url: '',
                      version: '1.0',
                      // risk
                      transparency: '',
                      fairness: '',
                      accountability: true,
                      robustness: true,
                      safety: true,
                      privacy: '',
                      ai_rmf_domains: defaultProgramData?.ai_rmf_domains,
                      // training
                      training_definition:
                        defaultProgramData?.training_definition?.steps.map(
                          (step: any) => {
                            if (step.type === 'video') {
                              return {
                                id: step.id,
                                type: step.type,
                                title: step.title ?? '',
                                youtubeUrl: step.youtubeUrl ?? '',
                              };
                            }
                            if (step.type === 'quiz') {
                              return {
                                id: step.id,
                                type: step.type,
                                title: step.title ?? '',
                                settings: step.settings ?? {},
                                questions: (step.questions ?? []).map(
                                  (q: any) => ({
                                    id: q.id,
                                    type: q.type,
                                    prompt: q.prompt ?? '',
                                    explanation: q.explanation ?? '',
                                    ...(q.type === 'multiple_choice' && {
                                      options: (q.options ?? []).map(
                                        (opt: any) => ({
                                          id: opt.id,
                                          text: opt.text ?? '',
                                          isCorrect: opt.isCorrect ?? false,
                                        })
                                      ),
                                    }),
                                    ...(q.type === 'true_false' && {
                                      correctAnswer: q.correctAnswer ?? false,
                                    }),
                                  })
                                ),
                              };
                            }
                            return {
                              id: step.id,
                              type: step.type,
                              title: step.title ?? '',
                              content: step.content ?? '',
                            };
                          }
                        ),
                      // objectives
                      prerequisites: defaultProgramData?.prerequisites,
                      learning_objectives:
                        defaultProgramData?.learning_objectives,
                      // achievement
                      role: defaultProgramData?.target_roles,
                      customtype: '',
                      learningobjective: '',
                      // certification
                      recurrence_months: defaultProgramData?.recurrence_months,
                      certification_valid_months:
                        defaultProgramData?.certification_valid_months,
                    }
                  : {
                      // basic
                      title: '',
                      duration: '',
                      description: '',
                      likelihood: '',
                      format: '',
                      status: '',
                      mandatory: false,
                      video_url: '',
                      version: '1.0',
                      // risk
                      transparency: '',
                      fairness: '',
                      accountability: '',
                      robustness: '',
                      safety: '',
                      privacy: '',
                      ai_rmf_domains: '',
                      // training
                      training_definition: [],
                      // objectives
                      prerequisites: [],
                      learning_objectives: [],
                      // achievement
                      role: [],
                      customtype: '',
                      learningobjective: '',
                      // certification
                      recurrence_months: '',
                      certification_valid_months: '',
                      // custom
                      add_prerequisites: '',
                      add_learning_objectives: '',
                    }
              }
              onSubmit={(values, formik) =>
                handleSubmittedValues(values, formik)
              }
              validationSchema={TrainingProgramsScheme}
            >
              {({ handleSubmit, errors, isSubmitting, values }: any) => {
                console.log(isSubmitting);

                return (
                  <>
                    <Box className="add-training-wrapper">
                      <BasicInformationForm test={undefined} />

                      <RiskManagementForm test={undefined} />
                      <TrainingSteps
                        test={undefined}
                        trainingDefinition={
                          defaultProgramData?.training_definition.steps
                        }
                        errorMessages={
                          isSubmitted && errors?.training_definition
                        }
                      />
                      <TrainingContent
                        test={undefined}
                        errorMessageContent={isSubmitted && errors?.content}
                        errorMessagePrerequisites={
                          isSubmitted &&
                          values?.prerequisites.length < 1 &&
                          errors?.prerequisites
                        }
                        errorMessageObjectives={
                          isSubmitted &&
                          values?.learning_objectives.length < 1 &&
                          errors?.learning_objectives
                        }
                      />
                      <AchievementForm test={undefined} />
                      <CertificationForm test={undefined} />
                    </Box>
                    <Stack
                      direction="row-reverse"
                      className="button-training-programs"
                    >
                      <AppPrimaryButton
                        title={
                          purpose === 'editing'
                            ? 'Update Program'
                            : 'Create Program'
                        }
                        onClick={() => {
                          setIsSubmitted(true);
                          handleSubmit();
                        }}
                        disabled={isSubmitting}
                        isLoading={isSubmitting}
                        loadingText="Please Wait"
                      />
                      <AppPrimaryButtonOutlined
                        title="Cancel"
                        onClick={() => navigate(-1)}
                      />
                    </Stack>
                  </>
                );
              }}
            </Formik>
          </>
        )}
      </Box>
    </>
  );
};

export default AddTrainingPrograms;
